import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import type { CommunicationDetail } from "@/types/communications.ts";

const communicationQueries = {
  all: () => ["communications"] as const,
  detail: (id: string | undefined) =>
    queryOptions({
      queryKey: [...communicationQueries.all(), id] as const,
      queryFn: id
        ? async ({ signal }) => {
            const response = await api.get<CommunicationDetail>(`/api/a/communications/${id}`, {
              signal,
            });
            return response.data;
          }
        : skipToken,
      staleTime: 60_000,
    }),
  thread: (communicationId: string | undefined) =>
    queryOptions({
      queryKey: [...communicationQueries.all(), "thread", communicationId] as const,
      queryFn: communicationId
        ? async ({ signal }) => {
            const response = await api.get<{
              messages: CommunicationDetail[];
              hasMore: boolean;
            }>(`/api/a/communications/${communicationId}/thread`, { signal });
            return response.data;
          }
        : skipToken,
      staleTime: 60_000,
    }),
};

export const useCommunication = ({ id }: { id?: string }) => {
  const { data, isLoading, error } = useQuery(communicationQueries.detail(id));
  return { communication: data, isLoading, error };
};

export const useCommunicationThread = ({ communicationId }: { communicationId?: string }) => {
  const { data, isLoading, error } = useQuery(communicationQueries.thread(communicationId));
  return { messages: data?.messages, hasMore: data?.hasMore ?? false, isLoading, error };
};
